<template>
  <div class="leave-template px-2 py-2">
    <ListingHeader>
      <template v-slot:content>
        <v-flex class="pt-0 mxw-200 d-flex align-center">
          <h1 class="form-title d-flex">
            <v-select
              :disabled="pageLoading"
              :items="statusList"
              v-model="leaveStatusType"
              @change="getEngineeerLeaves(1)"
              hide-details
              item-color="cyan"
              class="pt-0 mt-0 listing-status border-0"
              item-text="text"
              item-value="value"
            >
              <template v-slot:selection="{ item }">
                <span
                  class="font-size-20 font-weight-700 text-uppercase"
                  style="color: #24326d; letter-spacing: 0.75px;"
                >
                  {{ item.text }}
                </span>
              </template>
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title v-text="item.text"></v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip :color="item.color" :text-color="item.textcolor">
                    <template v-if="item.value == 'all'">
                      {{ item.all_leaves || 0 }}
                    </template>
                    <template v-else>
                      {{ item.leave_status_count || 0 }}
                    </template>
                  </v-chip>
                </v-list-item-action>
              </template>
            </v-select>
          </h1>
        </v-flex>
        <v-flex class="pt-0 pr-0 justify-flex-end d-flex align-center">
          <div class="mx-2">
            <DateRangePicker
              placeholder="Data Range"
              v-model="dates"
              v-on:update:model-value="getEngineeerLeaves(1)"
              v-on:click:clear="clearDates"
            ></DateRangePicker>
          </div>
          <div class="mr-2">
            <v-text-field
              v-model.trim="search"
              v-on:click:clear="clearSearch"
              clearable
              dense
              filled
              flat
              placeholder="Search"
              hide-details
              solo
              :disabled="pageLoading"
              color="cyan"
              @keydown.enter="getEngineeerLeaves(1)"
              @keydown.tab="getEngineeerLeaves(1)"
            ></v-text-field>
          </div>
          <div class="my-2">
            <v-tooltip top content-class="custom-top-tooltip">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  depressed
                  v-bind="attrs"
                  v-on="on"
                  class="custom-bold-button white--text"
                  color="cyan"
                  tile
                  :disabled="pageLoading"
                  v-on:click="getEngineeerLeaves(1)"
                >
                  <v-icon>mdi-magnify</v-icon>
                </v-btn>
              </template>
              <span>Search</span>
            </v-tooltip>
          </div>
        </v-flex>
      </template>
    </ListingHeader>

    <perfect-scrollbar
      :options="{ suppressScrollX: true }"
      class="scroll custom-box-top-inner-shadow"
      style="max-height: 90vh; position: relative"
    >
      <ListingTable
        :columnCount="6"
        :dataLoading="pageLoading"
        :rowData="filteredLogs"
      >
        <template v-slot:thead>
          <thead>
            <tr>
              <th class="simple-table-th">Leave #</th>
              <th class="simple-table-th">Start Date</th>
              <th class="simple-table-th">End Date</th>
              <th class="simple-table-th">Half Day</th>
              <th class="simple-table-th">Leave Duration</th>
              <th class="simple-table-th">Status</th>
            </tr>
          </thead>
        </template>
        <template v-slot:tbody>
          <tbody class="custom-border-bottom">
            <template v-if="filteredLogs.length > 0">
              <tr
                v-for="(leave, index) in filteredLogs"
                :key="index"
                class="customer-listing-row leave-listing-row"
                :class="{
                  'custom-border-top': index == 0,
                  'orange_lighten-5': index % 2 == 0,
                }"
              >
                <td class="simple-table-td font-size-14 font-weight-500 text-capitalize">
                  <Barcode :barcode="leave.barcode" :route="'admin.leave.detail'" :id="leave.id"></Barcode>
                </td>
                <td class="simple-table-td font-size-14 font-weight-500">
                  <template v-if="leave && leave.start_date">
                    <v-chip label color="green" outlined small class="font-weight-600">
                      {{ formatDate(leave.start_date) }}
                    </v-chip>
                  </template>
                  <em v-else class="text-muted"> no start date</em>
                </td>
                <td class="simple-table-td font-size-14 font-weight-500">
                  <template v-if="leave && leave.end_date">
                    <v-chip label color="red" outlined small class="font-weight-600">
                      {{ formatDate(leave.end_date) }}
                    </v-chip>
                  </template>
                  <em v-else class="text-muted"> no end date</em>
                </td>
                <td class="simple-table-td font-size-14 font-weight-500">
                  <template v-if="leave && leave?.duration">
                    <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                      <div class="halfday-block">
                        <b class="d-inline-block font-weight-700">First:</b>
                        <span v-if="leave?.first_half_of_end_date">
                          {{ formatDate(leave.end_date) }}
                        </span>
                        <em class="text-muted ml-1" v-else>not provided</em>
                      </div>

                       <div class="halfday-block">
                        <b class="d-inline-block font-weight-700">Second:</b>
                        <span v-if="leave?.second_half_start_date">
                          {{ formatDate(leave.start_date) }}
                        </span>
                        <em class="text-muted ml-1" v-else>not provided</em>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <em class="text-muted">no half day</em>
                  </template>
                </td>
                <td class="simple-table-td font-size-14 font-weight-500">
                  <template v-if="leave && leave.total_leave_days">
                    <v-avatar color="green" size="24" class="mr-1">
                      <span class="white--text font-weight-600 font-size-12">
                        {{ leave.total_leave_days }}
                      </span>
                    </v-avatar>
                    <span>{{ leave.total_leave_days == 1 ? 'Day' : 'Days' }}</span>
                  </template>
                  <em v-else class="text-muted">0 Days</em>
                </td>
                <td class="simple-table-td font-size-14 font-weight-500">
                  <template v-if="leave && (leave.status || leave.status_text)">
                    <v-chip
                      style="height: 24px"
                      class="pl-3 pr-3 text-uppercase font-weight-600 font-size-11"
                      :color="getStatusColor(leave)"
                      outlined
                      label
                      small
                    >
                      {{ leave?.status_text }}
                    </v-chip>
                  </template>
                  <em v-else class="text-muted"> no status</em>
                </td>
              </tr>
            </template>
            <tr v-else-if="!pageLoading">
              <td :colspan="6" class="py-4">
                <p class="m-0 row-not-found text-center font-weight-500 font-size-16">
                  <img :src="$assetURL('media/error/empty.png')" class="row-not-found-image" />
                  Uhh... There are no leaves at the moment.
                </p>
              </td>
            </tr>
          </tbody>
        </template>
      </ListingTable>
    </perfect-scrollbar>

    <ListingFooter
      :dataLoading="pageLoading"
      :showingFrom="showingFrom"
      :showingTo="showingTo"
      :totalRows="totalRows"
      :currentPage="currentPage"
      :totalPages="totalPages"
      :removePage="true"
    ></ListingFooter>
    <Dialog
      v-if="statusDialog"
      :common-dialog="statusDialog"
      :dialog-width="640"
      isClass
    >
      <template v-slot:title>
        <div class="d-flex">Confirmed</div>
      </template>
      <template v-slot:body>
        <v-form v-model="formValid">
          <v-row class="delete-dialog">
            <v-col md="12" class="py-0 my-auto py-2">
              <div class="btx-div m-0" v-if="statusdata == 'approved'">
                <span class="font-weight-600">
                  <template v-if="visits && visits.visitBarcode">
                    <div style="font-size: 19px !important">
                      Assigned Visits :-
                      <span class="font-weight-500 red--text mr-2">{{
                        visits.visitBarcode
                      }}</span>
                    </div>
                    <br />
                  </template>
                  <span style="font-size: 18px !important">
                    Are you sure, You want to Approve this Leave
                  </span>
                  <br /><br />

                  <v-chip outlined label color="green" class="ml-2"
                    ><span class="font-weight-600">{{
                      leaveBarcode
                    }}</span> </v-chip
                  >&nbsp;?
                </span>
              </div>
              <div class="btx-div m-0" v-if="statusdata == 'cancelled'">
                <span class="font-weight-600">
                  <template v-if="visits && visits.visitBarcode">
                    <div style="font-size: 19px !important">
                      Assigned Visits :-
                      <span class="font-weight-500 red--text mr-2">{{
                        visits.visitBarcode
                      }}</span>
                    </div>
                    <br />
                  </template>
                  <span style="font-size: 18px !important">
                    Are you sure, You want to Cancel this Leave
                  </span>
                  <v-chip outlined label color="teal" class="ml-2"
                    ><span class="font-weight-600">{{
                      leaveBarcode
                    }}</span> </v-chip
                  >&nbsp;?
                </span>
                <div class="mt-3" v-if="statusdata == 'cancelled'">
                  <label class="input-label required mb-1">Cancel Remark</label>
                  <v-textarea
                    auto-grow
                    dense
                    :rules="[
                      validateRules.required(cancelled_remark, 'Remark'),
                    ]"
                    filled
                    color="cyan"
                    v-model="cancelled_remark"
                    placeholder="Reason for cancellation"
                    solo
                    flat
                    row-height="15"
                  ></v-textarea>
                </div>
              </div>
              <div v-if="statusdata == 'rejected'">
                <span class="font-weight-600">
                  <span style="font-size: 19px !important">
                    Are you sure, You want to Reject this Leave
                  </span>

                  <!--  {{ leaveId.length > 1 ? "these leaves" : "this Leave" }} -->
                  <v-chip outlined label color="red" class="ml-2">
                    <span class="font-weight-600">{{
                      leaveBarcode
                    }}</span> </v-chip
                  >&nbsp; ?
                </span>

                <div class="mt-3" v-if="statusdata == 'rejected'">
                  <label class="input-label required mb-1">Reject Remark</label>
                  <v-textarea
                    auto-grow
                    dense
                    :rules="[validateRules.required(rejected_remark, 'Remark')]"
                    filled
                    color="cyan"
                    v-model="rejected_remark"
                    placeholder="Reason for leave"
                    solo
                    flat
                    row-height="15"
                  ></v-textarea>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-form>
      </template>
      <template v-slot:action>
        <v-btn
          class="mx-2 custom-grey-border custom-bold-button white--text"
          color="cyan"
          :disabled="!formValid"
          @click="dropData(visits.visitId)"
          style="min-width: 78px !important"
        >
          <template
            v-if="
              visits &&
              visits.visitId &&
              visits.visitId.length > 0 &&
              statusdata == 'approved'
            "
          >
            Approve & UnAssigned
          </template>
          <template v-else>
            <template v-if="statusdata == 'approved'"> Approve </template>
            <template v-if="statusdata == 'cancelled'"> Cancel </template>
            <template v-if="statusdata == 'rejected'"> Reject </template>
          </template>
        </v-btn>
        <v-btn
          class="mx-2 custom-grey-border custom-bold-button"
          @click="statusDialog = false"
        >
          Close
        </v-btn>
      </template>
    </Dialog>
    <AttachmentListModal
      :show="attachmentDialog"
      :attachments="selectedAttachments"
      v-on:close="attachmentDialog = false"
      @download="downloadAttachment"
    />
  </div>
</template>

<script>
import CommonMixin from "@/core/plugins/common-mixin";
import moment from "moment-timezone";
import DateRangePicker from "@/view/components/DateRangePicker.vue";

import JwtService from "@/core/services/jwt.service";
import { PATCH, QUERY } from "@/core/services/store/request.module";
import { LeaveApplyEventBus } from "@/core/lib/leave.from.lib.js";
import { PaginationEventBus } from "@/core/lib/pagination.lib";
import Dialog from "@/view/pages/partials/Dialog";
import ValidationMixin from "@/core/plugins/validation-mixin";
import ListingTable from "@/view/pages/partials/Listing-Table.vue";
import ListingHeader from "@/view/pages/partials/Listing-Header.vue";
import ListingFooter from "@/view/pages/partials/Listing-Footer.vue";
moment.tz.setDefault(process.env.VUE_APP_TIMEZONE);
import AttachmentListModal from "@/view/components/AttachmentListModal";
import Barcode from "@/view/pages/partials/Barcode.vue";

export default {
  mixins: [CommonMixin, ValidationMixin],
  name: "leaves-activity",
  props: {
    logs: {
      type: [Object, Array],
      required: false,
      default: () => {
        return new Array();
      },
    },
    user: {
      type: [Array, Object, Number, String],
      default: () => {
        return new Array();
      },
      required: false,
    },
    engineer: {
      type: [Number, String, Object],
      default: 0,
    },
  },
  watch: {
    engineer: {
      immediate: true,
      handler(param) {
        if (param) {
          this.getEngineeerLeaves();
        }
      },
    },
    user: {
      immediate: true,
      handler(param) {
        if (param) {
          this.getEngineeerLeaves();
        }
      },
    },
    leaveTypes: {
      deep: true,
      immediate: true,
      handler(param) {
        this.leaveTypes = param;
      },
    },
    logs: {
      deep: true,
      immediate: true,
      handler(param) {
        this.loglist = param;
        this.updateStatusCounts();
      },
    },
  },
  components: {
    Dialog,
    ListingTable,
    ListingHeader,
    ListingFooter,
    DateRangePicker,
    AttachmentListModal,
    Barcode,
  },
  data() {
    return {
      search: null,
      dates: [],
      currentPage: 1,
      perPage: 15,
      totalRows: 0,
      totalPages: 1,
      leaveId: null,
      duration: [],
      leaveStatusType: "all",
      attachmentDialog: false,
      selectedAttachments: [],
      visits: [],
      extensionArray: ["jpg", "png", "gif", "jpeg", "webp", "tiff", "jfif"],
      leaveTypes: [],
      leaveBarcode: null,
      CurrentUser: JwtService.currentUser(),
      statusDialog: false,
      detailDialog: false,
      statusdata: null,
      rejected_remark: null,
      cancelled_remark: null,
      pageLoading: false,
      statusList: [],

      statusDropdown: [
        {
          text: "Approved",
          value: "approved",
          color: "green",
          headercolor: "primary",
          status: "2",
        },
        {
          text: "Rejected",
          value: "rejected",
          color: "red",
          headercolor: "primary",
          status: "3",
        },
        {
          text: "Cancelled",
          value: "cancelled",
          color: "teal",
          headercolor: "primary",
          status: "1",
        },
      ],
      loglist: [],
    };
  },
  computed: {
    showingFrom() {
      if (this.totalRows === 0) return 0;
      return (this.currentPage - 1) * this.perPage + 1;
    },
    showingTo() {
      return Math.min(this.currentPage * this.perPage, this.totalRows);
    },
    filteredLogs() {
      return Array.isArray(this.loglist) ? this.loglist : [];
    },
  },
  methods: {
    formatRelativeTime(date) {
      return moment(date).fromNow();
    },
    getInitials(name) {
      if (!name) return "";
      const parts = name.split(" ");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      } else {
        return parts[0].substring(0, 2).toUpperCase();
      }
    },
    isImage(ext) {
      if (find(this.extensionArray, (row) => row == ext.toLowerCase())) {
        return true;
      } else {
        return false;
      }
    },
    formatFileSize(bytes) {
      if (!bytes) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },

    downloadAttachment(attachment) {
      window.open(attachment.file.url, "_blank");
    },
    showAttachments(attachments) {
      this.selectedAttachments = attachments;
      this.attachmentDialog = true;
    },
    isDisabled(leave) {
      return leave.status == 1 && this.CurrentUser.role == 1 ? false : true;
    },
    updateStatus(status, row) {
      this.statusDialog = true;
      this.leaveId = row.id;
      this.visits = row;
      this.visitBarcode = row.barcode;
      this.leaveBarcode = row.barcode;
      this.statusdata = status;
    },
    formatDateStartTime(row) {
      return moment(row).format("hh:mm A");
    },
    objectLength() {
      // Use Object.keys() to get an array of keys in the object
      return Object.keys(this.logs).length;
    },
    dropData(visit) {
      this.autoRefresh = false;
      this.pageLoading = true;
      let payload = {
        visit: visit,
        leaves: this.leaveId,
        status: this.statusdata, // 1: Pending, 2: Approved, 3: Rejected 4: cancelled
        remark:
          this.statusdata == "rejected"
            ? this.rejected_remark
            : this.statusdata == "cancelled"
            ? this.cancelled_remark
            : null,
      };
      this.$store
        .dispatch(PATCH, {
          url: `leave/${this.leaveId}/${this.statusdata}`,
          data: payload,
        })
        .then(() => {
          this.statusDialog = false;
          this.detailDialog = false;
          this.reject_remark = null;
          this.autoRefresh = true;
          LeaveApplyEventBus.$emit("refresh");
          this.$emit("refresh", true);
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.pageLoading = false;
        });
    },
    // getLeaveTypes() {
    //   const _this = this;
    //   _this.$store
    //     .dispatch(GET, { url: "leave-types?all=true" })
    //     .then(({ data }) => {
    //       _this.leaveTypes = data;
    //     })
    //     .catch((error) => {
    //       _this.logError(error);
    //     })
    //     .finally(() => {
    //       _this.pageLoading = false;
    //     });
    // },
    clearSearch() {
      this.search = null;
      this.getEngineeerLeaves(1);
    },
    clearDates() {
      this.dates = [];
      this.getEngineeerLeaves(1);
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.getEngineeerLeaves(page);
    },
    getStatusValue(type) {
      if (!type || type === "all") return null;
      if (Array.isArray(this.statusList) && this.statusList.length > 0) {
        const found = this.statusList.find(
          (s) => s.value == type || s.status == type
        );
        if (found && found.status !== undefined && Number(found.status) > 0) {
          return Number(found.status);
        }
      }
      if (type === "pending") return 1;
      if (type === "approved") return 2;
      if (type === "rejected") return 3;
      if (type === "cancelled") return 4;
      return null;
    },
    getEngineeerLeaves(page) {
      const _this = this;
      let engineerId = 0;
      if (_this.engineer) {
        engineerId =
          typeof _this.engineer === "object"
            ? _this.engineer.id
            : _this.engineer;
      }
      if (
        !engineerId &&
        _this.$route &&
        _this.$route.params &&
        _this.$route.params.id
      ) {
        engineerId = _this.$route.params.id;
      }
      if (!engineerId && _this.user) {
        engineerId =
          typeof _this.user === "object" ? _this.user.id : _this.user;
      }
      if (!engineerId) {
        return;
      }
      if (typeof page === "number") {
        _this.currentPage = page;
      }
      _this.pageLoading = true;
      const params = {
        current_page: _this.currentPage,
        per_page: _this.perPage,
        search: _this.search ? _this.search.trim() : null,
        dates: _this.dates && _this.dates.length > 0 ? _this.dates : null,
      };
      if (_this.leaveStatusType && _this.leaveStatusType !== "all") {
        params.status = _this.getStatusValue(_this.leaveStatusType);
      }
      _this.$store
        .dispatch(QUERY, {
          url: `engineer-all-leave/${engineerId}`,
          data: params,
        })
        .then(({ data }) => {
          _this.loglist = data?.rows || (Array.isArray(data) ? data : []);
          _this.totalRows = data?.total_rows ?? _this.loglist.length;
          _this.currentPage = data?.current_page ?? _this.currentPage;
          _this.perPage = data?.per_page ?? _this.perPage;
          _this.totalPages = Math.ceil((_this.totalRows || 1) / _this.perPage) || 1;
          if (Array.isArray(data?.status_list) && data.status_list.length > 0) {
            _this.statusList = data.status_list;
          }
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.pageLoading = false;
        });
    },
    getStatusColor(leave) {
      if (!leave) return "primary";
      if (leave.status == 1) return "orange";
      if (leave.status == 2) return "green";
      if (leave.status == 3) return "red";
      if (leave.status == 4) return "teal";
      return "primary";
    },
    updateStatusCounts() {
      const list = Array.isArray(this.loglist) ? this.loglist : [];
      this.statusCounts = {
        all: list.length,
        pending: list.filter((leave) => leave.status === 1).length,
        accepted: list.filter((leave) => leave.status === 2).length,
        rejected: list.filter((leave) => leave.status === 3).length,
        cancelled: list.filter((leave) => leave.status === 4).length,
      };
    },

    redirectToDetail(leave) {
      this.$router.push({
        name: "admin.leave.detail",
        params: { id: leave.id },
      });
    },
  },

  mounted() {
    this.getEngineeerLeaves();
    PaginationEventBus.$on("update:pagination", (param) => {
      this.getEngineeerLeaves(param);
    });
  },
  beforeDestroy() {
    PaginationEventBus.$off("update:pagination");
  },
};
</script>
